import type { MarlinStoreDb } from "./db";
import type {
  CageRecord,
  ScreenLayout,
  ScreenLayoutTemplate,
  SelectionRecord,
  TemplateRecord,
} from "../types";

export type StateSchema = {
  screenLayouts: ScreenLayout[];
  machinesLoaded: Record<number, CageRecord[]>;
  selected: SelectionRecord | null;
  selectedMultiScreenTemplateId: string | null;
  multiScreenLayoutTemplates: TemplateRecord[];
  screenLayoutTemplates: ScreenLayoutTemplate[];
  selectedScreenTemplateIds: Record<string, string | null>;
};

export type StateKey = keyof StateSchema;

export type SyncStore = {
  get<K extends StateKey>(key: K): StateSchema[K];
  set<K extends StateKey>(key: K, value: StateSchema[K]): void;
};

const TABLES_FOR_KEY: Record<StateKey, (db: MarlinStoreDb) => string[]> = {
  screenLayouts: () => ["screens"],
  machinesLoaded: () => ["machines"],
  selected: () => ["settings"],
  selectedMultiScreenTemplateId: () => ["settings"],
  multiScreenLayoutTemplates: () => ["templates"],
  screenLayoutTemplates: () => ["screenTemplates"],
  selectedScreenTemplateIds: () => ["settings"],
};

export const tablesFor = (
  db: MarlinStoreDb,
  keys: readonly StateKey[],
): string[] => [...new Set(keys.flatMap((key) => TABLES_FOR_KEY[key](db)))];

const SELECTED_TEMPLATE_KEY = "selectedMultiScreenTemplateId";

export const SELECTED_CAMERA_KEY = "selectedCamera";

const hydrate = async <K extends StateKey>(
  db: MarlinStoreDb,
  key: K,
): Promise<StateSchema[K]> => {
  switch (key) {
    case "screenLayouts":
      return (await db.screens.orderBy("id").toArray()) as StateSchema[K];
    case "machinesLoaded": {
      const loaded: Record<number, CageRecord[]> = {};
      for (const machine of await db.machines.toArray())
        loaded[machine.machine_id] = machine.cages;
      return loaded as StateSchema[K];
    }
    case "selected":
      return ((await db.settings.get(SELECTED_CAMERA_KEY))?.value ??
        null) as StateSchema[K];
    case "selectedMultiScreenTemplateId":
      return ((await db.settings.get(SELECTED_TEMPLATE_KEY))?.value ??
        null) as StateSchema[K];
    case "multiScreenLayoutTemplates":
      return (await db.templates.toArray()) as StateSchema[K];
    case "screenLayoutTemplates":
      return (await db.screenTemplates.toArray()) as StateSchema[K];
    case "selectedScreenTemplateIds":
      return ((await db.settings.get("selectedScreenTemplateIds"))?.value ??
        {}) as StateSchema[K];
  }
  throw new Error(`unknown state key ${String(key)}`);
};

const flush = async <K extends StateKey>(
  db: MarlinStoreDb,
  key: K,
  value: StateSchema[K],
): Promise<void> => {
  switch (key) {
    case "screenLayouts": {
      const layouts = value as ScreenLayout[];
      const stored = new Map(
        (await db.screens.toArray()).map((row) => [
          row.id,
          JSON.stringify(row),
        ]),
      );
      const keep = new Set(layouts.map((layout) => layout.id));
      await db.screens.bulkDelete(
        [...stored.keys()].filter((id) => !keep.has(id)),
      );
      await db.screens.bulkPut(
        layouts.filter(
          (layout) => JSON.stringify(layout) !== stored.get(layout.id),
        ),
      );
      return;
    }
    case "machinesLoaded": {
      const loaded = value as Record<number, CageRecord[]>;
      for (const machine of await db.machines.toArray()) {
        if (!machine.machine_id) continue;
        const cages = loaded[machine.machine_id] ?? [];
        if (JSON.stringify(cages) !== JSON.stringify(machine.cages))
          await db.machines.put({ ...machine, cages });
      }
      return;
    }
    case "selected": {
      if (value) await db.settings.put({ key: SELECTED_CAMERA_KEY, value });
      else await db.settings.delete(SELECTED_CAMERA_KEY);
      return;
    }
    case "selectedMultiScreenTemplateId":
      await db.settings.put({
        key: SELECTED_TEMPLATE_KEY,
        value: value ?? null,
      });
      return;
    case "multiScreenLayoutTemplates": {
      const templates = value as TemplateRecord[];
      const keep = new Set(templates.map((template) => template.id));
      const existing = await db.templates.toCollection().primaryKeys();
      await db.templates.bulkDelete(existing.filter((id) => !keep.has(id)));
      await db.templates.bulkPut(templates);
      return;
    }
    case "screenLayoutTemplates": {
      const templates = value as ScreenLayoutTemplate[];
      const keep = new Set(templates.map((template) => template.id));
      const existing = await db.screenTemplates.toCollection().primaryKeys();
      await db.screenTemplates.bulkDelete(
        existing.filter((id) => !keep.has(id)),
      );
      await db.screenTemplates.bulkPut(templates);
      return;
    }
    case "selectedScreenTemplateIds":
      await db.settings.put({ key: "selectedScreenTemplateIds", value });
      return;
  }
};

export const withState = async <K extends StateKey, R>(
  db: MarlinStoreDb,
  keys: readonly K[],
  fn: (store: SyncStore) => R,
  extraTables: string[] = [],
): Promise<Awaited<R>> =>
  (await db.transaction(
    "rw",
    [...tablesFor(db, keys), ...extraTables],
    async () => {
      const snapshot = {} as Pick<StateSchema, K>;
      for (const key of keys) snapshot[key] = await hydrate(db, key);
      const stored = new Map(
        keys.map((key) => [key, JSON.stringify(snapshot[key])]),
      );
      const dirty = new Set<K>();
      const store: SyncStore = {
        get: (key) => {
          if (!(key in snapshot))
            throw new Error(`state key ${key} was not hydrated`);
          return snapshot[key as unknown as K] as never;
        },
        set: (key, value) => {
          if (!(key in snapshot))
            throw new Error(`state key ${key} was not hydrated`);
          snapshot[key as unknown as K] = value as never;
          dirty.add(key as unknown as K);
        },
      };
      const result = await fn(store);
      for (const key of dirty)
        if (JSON.stringify(snapshot[key]) !== stored.get(key))
          await flush(db, key, snapshot[key]);
      return result;
    },
  )) as Awaited<R>;

export const readState = async <K extends StateKey>(
  db: MarlinStoreDb,
  keys: readonly K[],
): Promise<Pick<StateSchema, K>> =>
  db.transaction("r", tablesFor(db, keys), async () => {
    const snapshot = {} as Pick<StateSchema, K>;
    for (const key of keys) snapshot[key] = await hydrate(db, key);
    return snapshot;
  });
